<script lang="ts" setup>
import type { Category, Item, Product } from '~/models';

import { useNuxtApp } from '#app';

import MadeWithLove from '~/components/common/MadeWithLove.vue';
import CartWrapper from '~/components/menu/CartWrapper.vue';
import CategoryList from '~/components/menu/CategoryList.vue';
import ItemAddons from '~/components/menu/ItemAddons.vue';
import ProductList from '~/components/menu/ProductList.vue';
import ProductListWrapper from '~/components/menu/ProductListWrapper.vue';
import RestaurantInfo, { type ChipInfo } from '~/components/menu/RestaurantInfo.vue';
import SubcategoryItem from '~/components/menu/SubcategoryItem.vue';

definePageMeta({
  layout: false
});

const route = useRoute();
const router = useRouter();
const notify = useNotify();
const { $device: { isMobile } } = useNuxtApp();

const preferenceStore = usePreferenceStore();
const categoryStore = useCategoryStore();
const productStore = useProductStore();
const voucherStore = useVoucherStore();
const authStore = useAuthStore();
const cartStore = useCartStore();

useSeoMeta({
  title: 'Menu'
});

const { data } = await useAsyncData('menu', async () => {
  const [responseCategories, responseProducts] = await Promise.all([
    categoryStore.list(),
    productStore.list()
  ]);

  const sort = (a: number, b: number): number => a > b ? 1 : a < b ? -1 : 0;

  const products: Product[] = responseProducts?.sort(
    ({ rank: a }, { rank: b }) => sort(a, b)
  ) ?? [];

  const categories: Category[] = responseCategories?.map((category) => ({
    ...category,
    products: products
      .filter((product) => product.category.uuid === category.uuid),
    children: category.children.map((child) => ({
      ...child,
      products: products
        .filter((product) => product.category.uuid === child.uuid)
    }))
  })).sort(
    ({ onlineRank: a }, { onlineRank: b }) => sort(a, b)
  ) ?? [];

  return {
    categories,
    products
  };
});

const categories = computed<Category[]>(() => {
  if (!data.value) {
    return [];
  }

  return [...data.value.categories].filter((category) => !category.parent);
});

const activeCategory = ref<Category | null>(isMobile ? null : categories.value[0]);
const activeProduct = ref<Product | null>(null);

const productListTitle = computed<string>(() => {
  return search.value
    ? `Search "${search.value}"`
    : activeCategory.value?.name ?? '';
});

const products = computed<Product[]>(() => {
  if (!data.value) {
    return [];
  }

  const list = [...data.value.products];

  if (search.value) {
    return list.filter((product) =>
      product.name.toUpperCase().includes(search.value.toUpperCase())
    );
  } else {
    return activeCategory.value?.products ?? [];
  }
});

const search = ref<string>('');
const displayCheckout = ref<boolean>(false);
const reveal = ref<boolean>(false);

const displayCustomHeader = computed<boolean>(() => {
  return isMobile && !!preferenceStore.preference.template.menuHeaderImageUrl;
});

const displayProductDialog = computed<boolean>(() => {
  return !!activeCategory.value || !!search.value;
});

const infoMessage = computed<string>(() => {
  if (!preferenceStore.isRestaurantOpened) {
    return 'Closed now.';
  }

  if (
    preferenceStore.preference.general.enableCollection &&
    !preferenceStore.inDeliveryTime
  ) {
    return 'Only Collections are available.';
  }

  return '';
});

const chip = computed<ChipInfo | undefined>(() => {
  if (!preferenceStore.isRestaurantOpened) {
    return {
      icon: 'ic:round-bookmark',
      label: `Open at ${preferenceStore.openAt}`
    };
  }

  if (
    preferenceStore.preference.general.enableCollection &&
    preferenceStore.preference.general.enableDelivery &&
    !preferenceStore.inDeliveryTime
  ) {
    return {
      icon: 'ic:round-delivery-dining',
      label: `Delivery starts at ${preferenceStore.deliveryStartsAt}`
    };
  }

  return undefined;
});

const checkoutLabel = computed<string>(() => {
  return `Checkout ${preferenceStore.moneySymbol} ${cartStore.total.toFixed(2)}`;
});

const onGoBooking = async (): Promise<void> => {
  await router.push({ name: 'booking' });
};

const onUpdateActiveProduct = (product: Product): void => {
  if (!preferenceStore.isRestaurantOpened) {
    notify.error('Sorry, online ordering not available.');
    return;
  }

  activeProduct.value = product;
};

const onConfirmItem = (item: Item): void => {
  cartStore.addItem(item);

  if (isMobile) {
    search.value = '';
    activeCategory.value = null;
  }
};

const onHideProducts = (): void => {
  activeCategory.value = null;
  search.value = '';
};

const onHideAddons = (): void => {
  activeProduct.value = null;
};

const updateHeaderReveal = (): void => {
  const layoutDefault = document.querySelector('.layout-default');

  if (!layoutDefault) {
    return;
  }

  if (layoutDefault.scrollTop >= 100) {
    reveal.value = true;
  } else {
    reveal.value = false;
  }
};

const handleParamVoucher = async (): Promise<void> => {
  if (!authStore.isAuthenticated) {
    return;
  }

  try {
    const code = route.query.voucher?.toString();

    if (!code) {
      return;
    }

    const voucher = await voucherStore.get(code);

    cartStore.setVoucher(voucher);
  } catch (e) {
    let message = '';

    if (e instanceof APIError) {
      message = e.code === 404 ? 'Voucher not found' : e.message;
    } else {
      message = (e as Error).message;
    }

    if (message) {
      notify.error(message);
    }
  }
};

cartStore.setServiceCharge(0);
cartStore.clearOrderDiscount();

handleParamVoucher();

onMounted(() => {
  const layoutDefault = document.querySelector('.layout-default');

  if (layoutDefault) {
    layoutDefault.addEventListener('scroll', updateHeaderReveal);
  }
});

onUnmounted(() => {
  const layoutDefault = document.querySelector('.layout-default');

  if (layoutDefault) {
    layoutDefault.removeEventListener('scroll', updateHeaderReveal);
  }
});
</script>

<template>
  <NuxtLayout name="default">
    <template
      v-if="displayCustomHeader"
      #header="{ onGoHome, onShowDrawer }"
    >
      <section
        class="menu__header shadow-lg"
        :class="{ reveal }"
        @click="onGoHome"
      >
        <UContainer class="header__container">
          <div class="h-header flex items-center w-full">
            <UButton
              class="text-inherit rounded-full hover:bg-header"
              variant="ghost"
              data-test="drawer"
              aria-label="drawer"
              @click.stop="onShowDrawer"
            >
              <template #leading>
                <UIcon
                  name="ic:round-menu"
                  class="text-2xl text-inherit"
                />
              </template>
            </UButton>
          </div>
        </UContainer>

        <RestaurantInfo
          v-if="infoMessage"
          :chip="chip"
          :message="infoMessage"
          square
        />
      </section>
    </template>

    <UContainer class="menu min-h-svh">
      <!-- SEO Headings -->
      <h1 v-show="false">Menu</h1>

      <RestaurantInfo
        v-if="!displayCustomHeader && infoMessage"
        :chip="chip"
        :message="infoMessage"
        :square="isMobile"
        class="menu__info"
      />

      <div class="menu__content">
        <CategoryList
          v-model:active="activeCategory"
          :categories="categories"
          class="menu__lside"
          @update:active="search = ''"
        >
          <UButton
            v-if="isMobile && preferenceStore.preference.general.enableBooking"
            block
            class="bg-company-1 uppercase py-2.5 font-semibold"
            label="Make a booking"
            @click="onGoBooking()"
          />

          <UInput
            v-model="search"
            icon="ic:round-search"
            placeholder="Search menu"
            size="xl"
          />
        </CategoryList>

        <ProductListWrapper
          :active="activeProduct"
          :model-value="displayProductDialog"
          :title="productListTitle"
          :products="products"
          class="menu__middle"
          @update:active="onUpdateActiveProduct"
          @update:model-value="onHideProducts()"
        >
          <template
            v-if="activeCategory?.description"
            #caption
          >
            <div
              class="py-2 font-semibold"
              :class="{
                'px-4 z-10 bg-card border-b border-card text-sm': isMobile,
                'px-2 border-t-2 text-xs border-company-1 w-full': !isMobile
              }"
            >
              <span class="subtext-card">{{ activeCategory.description }}</span>
            </div>
          </template>

          <UInput
            v-if="isMobile && !!search"
            v-model="search"
            v-focus
            icon="ic:round-search"
            placeholder="Search menu"
            size="xl"
            class="mx-2"
          />

          <SubcategoryItem
            v-for="subcategory of activeCategory?.children"
            :key="subcategory.uuid"
            :subcategory="subcategory"
            :class="{ 'mx-2': isMobile }"
          >
            <ProductList
              :active="activeProduct"
              :products="subcategory.products"
              :style="{
                marginInline: '0.5rem',
                paddingTop: '0'
              }"
              @update:active="onUpdateActiveProduct"
            />
          </SubcategoryItem>

          <UModal
            v-if="activeProduct"
            :model-value="!!activeProduct"
            prevent-close
            :fullscreen="!!isMobile"
            @update:model-value="onHideAddons()"
          >
            <ItemAddons
              :product="activeProduct"
              :fullscreen="!!isMobile"
              @confirm="onConfirmItem"
              @close="onHideAddons()"
            />
          </UModal>
        </ProductListWrapper>

        <CartWrapper
          v-model="displayCheckout"
          class="menu__rside"
          @hide="displayCheckout = false"
        />
      </div>
    </UContainer>

    <template
      v-if="isMobile"
      #footer
    >
      <div class="menu__footer bg-footer">
        <UButton
          block
          class="bg-company-1 font-semibold uppercase text-sm py-4"
          :label="checkoutLabel"
          :disabled="cartStore.items.length === 0"
          @click="displayCheckout = true"
        />

        <MadeWithLove />
      </div>
    </template>
  </NuxtLayout>
</template>

<style lang="scss" scoped>
body.mobile {
  .menu {
    padding-top: var(--header-height);
    padding-bottom: 0;
    padding-inline: 0;

    &__info {
      position: sticky;
      top: var(--header-height);
      z-index: 10;
    }
  }

  .layout-default:has(.menu__header) {
    .menu {
      padding-top: 13rem;
    }
  }
}

.menu {
  padding-top: calc(var(--header-height) + 1rem);
  padding-bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &__content {
    display: flex;
    gap: 0.5rem;
  }

  &__header {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1;
    width: 100%;
    min-height: 13rem;
    transition: min-height 0.3s ease;
    display: flex;
    flex-direction: column;
    color: var(--header-text-color);
    background-image: var(--menu-header-bg-image);
    background-color: transparent;
    background-blend-mode: multiply;
    background-repeat: no-repeat,
      repeat;
    background-size: cover;
    background-position: center center;

    &.reveal {
      transform: translateY(-110%);
      transition: all 0.3s ease-in-out;
    }

    &>.header__container {
      display: flex;
      align-items: baseline;
      width: 100%;
      flex: 1;
    }
  }

  &__lside {
    flex: 5;
  }

  &__middle {
    flex: 8;
  }

  &__rside {
    flex: 5;
  }

  &__footer {
    position: sticky;
    bottom: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    padding: 0.5rem;
    gap: 0.5rem;
    text-align: center;
  }
}
</style>
